import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { bbsPosts } from '@/mocks/bbsPosts';

const categories = ['全部', '公告', '脑机接口', '量子计算', '生活体验', 'Web5.0', '未来展望', '6G体验', '社交'];

const boards = [
  { id: 'hot', icon: 'ri-fire-line', color: '#FF6B35', label: '热门讨论', count: '12,840', bg: 'rgba(255,107,53,0.1)' },
  { id: 'tech', icon: 'ri-cpu-line', color: '#00FFCC', label: '技术前沿', count: '8,620', bg: 'rgba(0,255,204,0.1)' },
  { id: 'life', icon: 'ri-home-heart-line', color: '#FF6B9D', label: '元宇宙生活', count: '6,340', bg: 'rgba(255,107,157,0.1)' },
  { id: 'invest', icon: 'ri-line-chart-line', color: '#FFD700', label: '投资生态', count: '4,180', bg: 'rgba(255,215,0,0.1)' },
  { id: 'bci', icon: 'ri-mental-health-line', color: '#A855F7', label: '脑机接口', count: '3,920', bg: 'rgba(168,85,247,0.1)' },
  { id: 'quantum', icon: 'ri-bubble-chart-line', color: '#00FF88', label: '量子世界', count: '2,760', bg: 'rgba(0,255,136,0.1)' },
];

const hotTopics = [
  { title: '2030年你最期待哪项技术？', replies: 1240, color: '#FF6B35', trend: '+128' },
  { title: '脑机接口会改变婚姻关系吗？', replies: 986, color: '#A855F7', trend: '+96' },
  { title: '量子计算机会让比特币消失吗？', replies: 834, color: '#FFD700', trend: '+74' },
  { title: '元宇宙里的房产值得投资吗？', replies: 762, color: '#00FFCC', trend: '+61' },
  { title: '6G时代还需要WiFi吗？', replies: 641, color: '#00FF88', trend: '+52' },
];

const onlineMembers = [
  'https://public.readdy.ai/ai/img_res/cd5c29638e18ab1c624ae4ee39bfc1f6.jpg',
  'https://readdy.ai/api/search-image?query=young%20woman%20portrait%20professional%20dark%20background&width=40&height=40&seq=bbs-member-2&orientation=squarish',
  'https://readdy.ai/api/search-image?query=middle%20aged%20man%20portrait%20professional%20dark%20background&width=40&height=40&seq=bbs-member-3&orientation=squarish',
  'https://readdy.ai/api/search-image?query=young%20asian%20woman%20portrait%20professional%20dark%20background&width=40&height=40&seq=bbs-member-4&orientation=squarish',
];

export default function BBS() {
  const [activeCategory, setActiveCategory] = useState('全部');
  const [activeBoard, setActiveBoard] = useState('hot');
  const [activePost, setActivePost] = useState<typeof bbsPosts[0] | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();

  const filtered = activeCategory === '全部'
    ? bbsPosts
    : bbsPosts.filter((p) => p.category === activeCategory);

  const currentBoard = boards.find((b) => b.id === activeBoard)!;

  return (
    <section id="bbs" className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(180deg, #040810 0%, #050A12 50%, #040810 100%)' }}>
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=metaverse%20community%20forum%20digital%20space%20glowing%20nodes%20connections%20people%20avatars%20dark%20background%20futuristic%20sci-fi%20ultra%20wide%20cinematic%208k&width=1920&height=1080&seq=bbs-bg-v2-001&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-4"
        />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(255,107,157,0.04) 0%, transparent 60%)' }} />
        <div className="absolute inset-0 opacity-6" style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }} />
        <svg className="absolute top-0 left-0 w-full" height="4" viewBox="0 0 1440 4" preserveAspectRatio="none">
          <path d="M0,2 Q180,0 360,2 Q540,4 720,2 Q900,0 1080,2 Q1260,4 1440,2" stroke="#FF6B9D" strokeWidth="1.5" fill="none" strokeOpacity="0.3"/>
        </svg>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="mb-14"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div>
              <div
                className="inline-flex items-center gap-2 px-5 py-2 mb-6"
                style={{
                  background: 'rgba(255,107,157,0.1)',
                  border: '1.5px solid rgba(255,107,157,0.35)',
                  borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                  color: '#FF6B9D',
                  fontFamily: 'Caveat, cursive',
                  fontSize: '1rem',
                }}
              >
                <i className="ri-discuss-line" />
                元界星图 · MetaStarMap BBS
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                元界
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B9D] via-[#FFD700] to-[#00FFCC]"> 星图</span>
              </h2>
              <p className="text-white/40 text-base max-w-xl">
                全球元宇宙爱好者、从业者、研究者的交流家园 · 探讨前沿科技 · 分享真实体验
              </p>
            </div>

            {/* Stats Row */}
            <div className="flex flex-wrap gap-6">
              {[
                { label: '注册会员', value: '128,600+', color: '#00FF88', icon: 'ri-user-line' },
                { label: '讨论帖子', value: '45,200+', color: '#FFD700', icon: 'ri-chat-3-line' },
                { label: '今日活跃', value: '3,840', color: '#FF6B9D', icon: 'ri-pulse-line' },
                { label: '版块数量', value: '24', color: '#00FFCC', icon: 'ri-layout-grid-line' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="flex items-center gap-3 px-4 py-3"
                  style={{
                    background: `${stat.color}08`,
                    border: `1px solid ${stat.color}20`,
                    borderRadius: '12px 8px 14px 10px / 8px 14px 10px 12px',
                  }}
                >
                  <div
                    className="w-9 h-9 flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `${stat.color}15`,
                      border: `1px solid ${stat.color}35`,
                      borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                    }}
                  >
                    <i className={`${stat.icon} text-sm`} style={{ color: stat.color }} />
                  </div>
                  <div>
                    <p className="font-black text-lg leading-none" style={{ color: stat.color, fontFamily: 'Orbitron, sans-serif' }}>{stat.value}</p>
                    <p className="text-white/30 text-xs mt-0.5">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Board Navigation - Modern Card Style */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
          {boards.map((board) => (
            <button
              key={board.id}
              onClick={() => setActiveBoard(board.id)}
              className="group relative flex flex-col items-center gap-2.5 p-5 transition-all duration-300 cursor-pointer overflow-hidden"
              style={{
                background: activeBoard === board.id ? board.bg : 'rgba(255,255,255,0.02)',
                border: `2px solid ${activeBoard === board.id ? board.color + '50' : 'rgba(255,255,255,0.07)'}`,
                borderRadius: '16px 12px 18px 14px / 12px 18px 14px 16px',
                transform: activeBoard === board.id ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
                boxShadow: activeBoard === board.id ? `0 8px 30px ${board.color}20` : 'none',
              }}
            >
              {/* Glow effect */}
              {activeBoard === board.id && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ background: `radial-gradient(ellipse at 50% 0%, ${board.color}15 0%, transparent 70%)` }}
                />
              )}
              <div
                className="relative w-12 h-12 flex items-center justify-center transition-all duration-300"
                style={{
                  background: activeBoard === board.id ? `${board.color}20` : 'rgba(255,255,255,0.05)',
                  border: `1.5px solid ${activeBoard === board.id ? board.color + '50' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                  transform: activeBoard === board.id ? 'scale(1.1)' : 'scale(1)',
                }}
              >
                <i className={`${board.icon} text-xl`} style={{ color: activeBoard === board.id ? board.color : 'rgba(255,255,255,0.3)' }} />
              </div>
              <p
                className="text-sm font-bold text-center leading-tight"
                style={{
                  color: activeBoard === board.id ? board.color : 'rgba(255,255,255,0.4)',
                  fontFamily: 'Caveat, cursive',
                  fontSize: '0.9rem',
                }}
              >
                {board.label}
              </p>
              <div
                className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs"
                style={{
                  background: activeBoard === board.id ? `${board.color}15` : 'rgba(255,255,255,0.04)',
                  color: activeBoard === board.id ? board.color : 'rgba(255,255,255,0.2)',
                  border: `1px solid ${activeBoard === board.id ? board.color + '30' : 'rgba(255,255,255,0.06)'}`,
                }}
              >
                <i className="ri-chat-1-line text-xs" />
                {board.count}
              </div>
              {/* Bottom accent */}
              {activeBoard === board.id && (
                <div
                  className="absolute bottom-0 left-4 right-4 h-0.5 rounded-full"
                  style={{ background: `linear-gradient(90deg, transparent, ${board.color}, transparent)` }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Main Content - 3 Column Layout */}
        <div className="flex flex-col xl:flex-row gap-8">
          {/* Post List - Main Column */}
          <div className="flex-1 min-w-0">
            {/* Board Header + Category Filter */}
            <div
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 p-4 rounded-2xl"
              style={{
                background: `${currentBoard.color}06`,
                border: `1px solid ${currentBoard.color}20`,
                borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px',
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 flex items-center justify-center"
                  style={{
                    background: `${currentBoard.color}20`,
                    border: `1.5px solid ${currentBoard.color}50`,
                    borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                  }}
                >
                  <i className={`${currentBoard.icon} text-base`} style={{ color: currentBoard.color }} />
                </div>
                <div>
                  <h3 className="text-white font-bold text-base" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.05rem' }}>{currentBoard.label}</h3>
                  <p className="text-white/30 text-xs">{currentBoard.count} 条讨论</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {categories.slice(0, 6).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className="px-3 py-1 text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
                    style={{
                      background: activeCategory === cat ? `${currentBoard.color}20` : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${activeCategory === cat ? currentBoard.color + '50' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                      color: activeCategory === cat ? currentBoard.color : 'rgba(255,255,255,0.35)',
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Posts - Modern Card Style */}
            <div className="flex flex-col gap-3">
              {filtered.map((post) => (
                <div key={post.id}>
                  <div
                    className="group relative flex items-start gap-4 p-5 transition-all duration-300 cursor-pointer overflow-hidden"
                    style={{
                      background: activePost?.id === post.id ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${activePost?.id === post.id ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.06)'}`,
                      borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px',
                      transform: activePost?.id === post.id ? 'none' : 'translateX(0)',
                    }}
                    onClick={() => setActivePost(activePost?.id === post.id ? null : post)}
                  >
                    {/* Left accent bar */}
                    <div
                      className="absolute left-0 top-4 bottom-4 w-0.5 rounded-full transition-all duration-300"
                      style={{
                        background: post.isHot ? '#FF6B35' : post.isPinned ? '#FFD700' : 'rgba(255,255,255,0.1)',
                        opacity: activePost?.id === post.id || post.isHot || post.isPinned ? 1 : 0,
                      }}
                    />

                    {/* Avatar */}
                    <div
                      className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 transition-all duration-300 group-hover:scale-105"
                      style={{ border: '2px solid rgba(255,255,255,0.1)' }}
                    >
                      <img src={post.avatar} alt={post.author} className="w-full h-full object-cover object-top" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        {post.isPinned && (
                          <span
                            className="text-xs px-2 py-0.5 font-bold"
                            style={{
                              background: 'rgba(255,215,0,0.12)',
                              color: '#FFD700',
                              border: '1px solid rgba(255,215,0,0.3)',
                              borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                              fontFamily: 'Caveat, cursive',
                            }}
                          >
                            📌 置顶
                          </span>
                        )}
                        {post.isHot && (
                          <span
                            className="text-xs px-2 py-0.5 font-bold"
                            style={{
                              background: 'rgba(255,107,53,0.12)',
                              color: '#FF6B35',
                              border: '1px solid rgba(255,107,53,0.3)',
                              borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                              fontFamily: 'Caveat, cursive',
                            }}
                          >
                            🔥 热门
                          </span>
                        )}
                        <span
                          className="text-xs px-2 py-0.5"
                          style={{
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: 'rgba(255,255,255,0.3)',
                            borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                          }}
                        >
                          {post.category}
                        </span>
                      </div>
                      <h4 className="text-white/85 text-sm font-semibold group-hover:text-white transition-colors line-clamp-1 mb-1">{post.title}</h4>
                      <p className="text-white/30 text-xs line-clamp-1">{post.preview}</p>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-5 flex-shrink-0">
                      <div className="text-center hidden sm:block">
                        <p className="text-white/65 text-sm font-bold">{post.replies}</p>
                        <p className="text-white/20 text-xs">回复</p>
                      </div>
                      <div className="text-center hidden md:block">
                        <p className="text-white/65 text-sm font-bold">{post.views.toLocaleString()}</p>
                        <p className="text-white/20 text-xs">浏览</p>
                      </div>
                      <div className="text-center hidden lg:block">
                        <p className="text-white/40 text-xs font-medium">{post.author}</p>
                        <p className="text-white/20 text-xs">{post.lastReply}</p>
                      </div>
                      <div
                        className="w-7 h-7 flex items-center justify-center transition-all duration-300"
                        style={{
                          background: activePost?.id === post.id ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)',
                          borderRadius: '50%',
                          color: 'rgba(255,255,255,0.3)',
                        }}
                      >
                        <i className={`${activePost?.id === post.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`} />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Replies */}
                  {activePost?.id === post.id && post.replies_data.length > 0 && (
                    <div
                      className="mx-3 mb-2 p-5"
                      style={{
                        background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.07)',
                        borderRadius: '0 0 14px 14px',
                        borderTop: 'none',
                      }}
                    >
                      <p className="text-white/25 text-xs font-bold tracking-widest uppercase mb-4">精选回复</p>
                      <div className="flex flex-col gap-3">
                        {post.replies_data.map((reply, i) => (
                          <div
                            key={i}
                            className="flex gap-3 p-4"
                            style={{
                              background: 'rgba(255,255,255,0.02)',
                              border: '1px solid rgba(255,255,255,0.05)',
                              borderRadius: '10px 6px 12px 8px / 6px 12px 8px 10px',
                            }}
                          >
                            <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                              <img src={reply.avatar} alt={reply.author} className="w-full h-full object-cover object-top" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-1.5">
                                <span className="text-white/70 text-xs font-semibold">{reply.author}</span>
                                <span className="text-white/20 text-xs">{reply.date}</span>
                              </div>
                              <p className="text-white/50 text-xs leading-relaxed">{reply.content}</p>
                              <button className="flex items-center gap-1 text-white/25 text-xs hover:text-white/50 transition-colors cursor-pointer mt-2">
                                <i className="ri-thumb-up-line" />
                                <span>{reply.likes}</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Load More */}
            <div className="mt-6 text-center">
              <button
                className="inline-flex items-center gap-2 px-8 py-3 font-bold text-sm transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
                style={{
                  background: 'rgba(255,107,157,0.08)',
                  border: '1.5px solid rgba(255,107,157,0.3)',
                  borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                  color: '#FF6B9D',
                  fontFamily: 'Caveat, cursive',
                  fontSize: '1rem',
                }}
              >
                <i className="ri-refresh-line" />
                加载更多讨论
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="xl:w-72 flex-shrink-0 flex flex-col gap-5">
            {/* Join CTA */}
            <div
              className="relative p-6 text-center overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(0,255,136,0.08), rgba(255,215,0,0.06))',
                border: '2px solid rgba(0,255,136,0.25)',
                borderRadius: '20px 14px 22px 16px / 14px 22px 16px 20px',
              }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 pointer-events-none opacity-8" style={{ background: 'radial-gradient(ellipse, rgba(0,255,136,0.6) 0%, transparent 70%)' }} />
              <div className="relative z-10">
                <div
                  className="w-16 h-16 flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: 'rgba(0,255,136,0.15)',
                    border: '2px solid rgba(0,255,136,0.4)',
                    borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                  }}
                >
                  <i className="ri-user-star-line text-[#00FF88] text-2xl" />
                </div>
                <p className="text-white font-bold text-base mb-2" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.1rem' }}>加入元界星图</p>
                <p className="text-white/40 text-xs leading-relaxed mb-4">与全球元宇宙先行者共同探索数字文明的未来</p>
                <div className="flex flex-col gap-2">
                  <button
                    className="flex items-center justify-center gap-2 px-5 py-2.5 text-black text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105"
                    style={{
                      background: 'linear-gradient(135deg, #00FF88, #00FFCC)',
                      borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                      fontFamily: 'Caveat, cursive',
                      fontSize: '1rem',
                    }}
                  >
                    <i className="ri-user-add-line" />
                    免费注册会员
                  </button>
                  <button
                    className="flex items-center justify-center gap-2 px-5 py-2.5 text-white/60 text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105"
                    style={{
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                    }}
                  >
                    <i className="ri-login-box-line" />
                    已有账号登录
                  </button>
                </div>
              </div>
            </div>

            {/* Hot Topics - Modern */}
            <div
              className="p-5"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '16px 12px 18px 14px / 12px 18px 14px 16px',
              }}
            >
              <div className="flex items-center gap-2 mb-5">
                <div className="w-6 h-6 flex items-center justify-center" style={{ background: 'rgba(255,107,53,0.2)', borderRadius: '50%' }}>
                  <i className="ri-fire-line text-xs text-[#FF6B35]" />
                </div>
                <p className="text-white/60 text-xs font-bold tracking-widest uppercase">本周热议</p>
              </div>
              <div className="flex flex-col gap-3">
                {hotTopics.map((topic, i) => (
                  <div key={i} className="flex items-start gap-3 cursor-pointer group">
                    <div
                      className="w-6 h-6 flex items-center justify-center text-xs font-black flex-shrink-0 mt-0.5"
                      style={{
                        background: i < 3 ? `${topic.color}20` : 'rgba(255,255,255,0.05)',
                        color: i < 3 ? topic.color : 'rgba(255,255,255,0.3)',
                        borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                        fontFamily: 'Orbitron, sans-serif',
                        fontSize: '0.65rem',
                      }}
                    >
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/60 text-xs leading-snug group-hover:text-white/90 transition-colors line-clamp-2">{topic.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-white/20 text-xs">{topic.replies} 回复</span>
                        <span className="text-xs font-bold" style={{ color: topic.color }}>{topic.trend}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Online Members */}
            <div
              className="p-5"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '16px 12px 18px 14px / 12px 18px 14px 16px',
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-[#00FF88] animate-pulse" />
                <p className="text-white/50 text-xs font-bold tracking-widest uppercase">在线会员</p>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex -space-x-2">
                  {onlineMembers.map((src, i) => (
                    <div key={i} className="w-8 h-8 rounded-full overflow-hidden" style={{ border: '2px solid #040810' }}>
                      <img src={src} alt="member" className="w-full h-full object-cover object-top" />
                    </div>
                  ))}
                </div>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white/50" style={{ background: 'rgba(255,255,255,0.08)', border: '2px solid #040810' }}>
                  +3K
                </div>
              </div>
              <p className="text-white/30 text-xs">当前 <span className="text-[#00FF88] font-bold">3,840</span> 人在线讨论</p>
            </div>

            {/* Quick Post */}
            <div
              className="p-5"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '16px 12px 18px 14px / 12px 18px 14px 16px',
              }}
            >
              <p className="text-white/50 text-xs font-bold tracking-widest uppercase mb-4">快速发帖</p>
              <textarea
                className="w-full bg-transparent border border-white/10 rounded-xl p-3 text-white/60 text-xs resize-none focus:outline-none focus:border-white/25 transition-colors"
                rows={3}
                placeholder="分享你对元宇宙的想法..."
                style={{ fontFamily: 'Caveat, cursive', fontSize: '0.85rem' }}
              />
              <button
                className="w-full mt-3 py-2.5 text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105"
                style={{
                  background: 'rgba(255,107,157,0.12)',
                  border: '1px solid rgba(255,107,157,0.3)',
                  borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                  color: '#FF6B9D',
                  fontFamily: 'Caveat, cursive',
                  fontSize: '0.95rem',
                }}
              >
                <i className="ri-send-plane-line mr-2" />
                发布讨论
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
